// Time formatting helpers. Two rules:
//   1. Render in viewer's local timezone using Intl.DateTimeFormat.
//   2. Always show the venue tz id as a small label so people physically at
//      the venue know the wall-clock time there.
//
// Defense-in-depth: both helpers swallow Intl.DateTimeFormat throws (#72 was
// a callsite passing an IANA tz id where a BCP-47 locale tag was expected,
// which RangeError'd out and white-screened the page). On any throw we fall
// back to ISO + tz suffix and post a diagnostic so we still hear about it.

import { reportDiagnostic } from './diagnostics.js'

export function formatLocalDateTime(iso, locale = undefined) {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(iso))
  } catch (err) {
    reportDiagnostic({
      severity: 'error',
      source: 'datetime.formatLocalDateTime',
      title: 'Intl.DateTimeFormat threw',
      message: String(err?.message || err),
      details: `iso=${iso} locale=${locale}`,
    })
    return new Date(iso).toISOString()
  }
}

export function formatVenueTime(iso, tzId, locale = undefined) {
  if (!iso || !tzId) return ''
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tzId,
      timeZoneName: 'short',
    }).format(new Date(iso))
  } catch (err) {
    reportDiagnostic({
      severity: 'error',
      source: 'datetime.formatVenueTime',
      title: 'Intl.DateTimeFormat threw',
      message: String(err?.message || err),
      details: `iso=${iso} tzId=${tzId} locale=${locale}`,
    })
    return `${new Date(iso).toISOString()} (${tzId})`
  }
}

// Cached IANA zone list, used as the <datalist> source on every free-text tz
// field. Intl.supportedValuesOf is Chromium 99+, Safari 15.4+, Firefox 93+.
// On older runtimes (and in some jsdom versions) the function is missing —
// fall back to an empty list and skip the picker.
export const IANA_TIME_ZONES = (() => {
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone')
    }
  } catch { /* fall through */ }
  return []
})()

// #379: the exact set of ids where Intl and Java disagree.
//
// The server validates with `ZoneId.of(tzId)` (AdminUserController.validateTz,
// EventController). These 28 ids are the keys of Java's `ZoneId.SHORT_IDS`:
// `ZoneId.of` rejects every one of them, while ICU — and therefore
// `Intl.DateTimeFormat` — resolves every one of them. That gap is the whole
// bug: a try/catch check on Intl waves `EST` through to a server that answers
// 400. Verified against both runtimes on 2026-09-09, and it is the *complete*
// divergence: `CET`, `GMT`, `UTC`, `US/Eastern`, `Etc/UTC`, `EST5EDT`,
// `Greenwich` and friends are accepted by both, so none of them may be
// rejected here even though `Intl.supportedValuesOf` omits them all.
//
// The value is the zone to offer instead — Java's own SHORT_IDS mapping, so
// the suggestion is the one the platform itself considers equivalent.
export const LEGACY_SHORT_ZONE_IDS = {
  ACT: 'Australia/Darwin',
  AET: 'Australia/Sydney',
  AGT: 'America/Argentina/Buenos_Aires',
  ART: 'Africa/Cairo',
  AST: 'America/Anchorage',
  BET: 'America/Sao_Paulo',
  BST: 'Asia/Dhaka',
  CAT: 'Africa/Harare',
  CNT: 'America/St_Johns',
  CST: 'America/Chicago',
  CTT: 'Asia/Shanghai',
  EAT: 'Africa/Addis_Ababa',
  ECT: 'Europe/Paris',
  EST: 'America/New_York',
  HST: 'Pacific/Honolulu',
  IET: 'America/Indiana/Indianapolis',
  IST: 'Asia/Kolkata',
  JST: 'Asia/Tokyo',
  MIT: 'Pacific/Apia',
  MST: 'America/Denver',
  NET: 'Asia/Yerevan',
  NST: 'Pacific/Auckland',
  PLT: 'Asia/Karachi',
  PNT: 'America/Phoenix',
  PRT: 'America/Puerto_Rico',
  PST: 'America/Los_Angeles',
  SST: 'Pacific/Guadalcanal',
  VST: 'Asia/Ho_Chi_Minh',
}

// True iff the given string is a tz id the SERVER will accept. Used by the
// edit form to decide when a free-text tzId field is "ready" to drive a tz
// re-render of the time inputs, and by both admin forms to reject a value
// before it costs a round trip.
//
// Deliberately not "is it in IANA_TIME_ZONES": that list is the 418 canonical
// zones, which excludes `UTC` — the default this app writes — along with
// `GMT`, `Etc/*` and the `US/*` links, all of which `ZoneId.of` accepts.
export function isValidTimeZone(tzId) {
  if (!tzId) return false
  if (Object.prototype.hasOwnProperty.call(LEGACY_SHORT_ZONE_IDS, tzId)) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tzId })
    return true
  } catch {
    return false
  }
}

// The zone to offer in place of a rejected one, or '' when there is no obvious
// candidate. Only the legacy short ids above have one — `paris` is a typo, not
// an alias, and guessing at it would be worse than saying nothing.
export function suggestTimeZone(tzId) {
  if (!tzId) return ''
  return LEGACY_SHORT_ZONE_IDS[tzId] || ''
}

// The sentence shown when a tz field is rejected client-side. Shared so the
// users page and the event form cannot drift apart in wording (#379).
export function timeZoneErrorMessage(tzId) {
  const suggestion = suggestTimeZone(tzId)
  return suggestion
    ? `\u201c${tzId}\u201d is not a valid IANA time zone \u2014 use \u201c${suggestion}\u201d instead.`
    : `\u201c${tzId}\u201d is not a valid IANA time zone \u2014 try a value like \u201cEurope/Paris\u201d or \u201cAmerica/New_York\u201d.`
}

// Offset (in ms) of `tzId` at the instant `utcMs`. Positive means the zone is
// east of UTC. Computed by asking Intl what the wall-clock at that instant
// looks like in `tzId`, treating that as if it were UTC, and diffing.
function tzOffsetMs(utcMs, tzId) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tzId,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const parts = Object.fromEntries(
    dtf.formatToParts(new Date(utcMs)).map((p) => [p.type, p.value])
  )
  const hh = parts.hour === '24' ? '00' : parts.hour
  const localAsUtcMs = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(hh), Number(parts.minute), Number(parts.second),
  )
  return localAsUtcMs - utcMs
}

// Convert a `<input type="datetime-local">` value (a wall-clock string with
// no zone) interpreted **at `tzId`** to a UTC ISO instant. Returns null on
// missing/bad input; falls back to interpreting as UTC and reports a
// diagnostic if Intl rejects the tzId.
//
// DST handling: we compute the offset twice — first at the naive UTC
// reading, then at the candidate instant — and use the second offset if it
// differs. That covers the spring-forward / fall-back boundary where the
// offset on the candidate side of the gap is the right one to apply.
export function instantFromVenueLocal(localStr, tzId) {
  if (!localStr) return null
  const m = String(localStr).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return null
  const [, y, mo, d, hh, mm] = m
  const naiveUtcMs = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm))
  if (!tzId) return new Date(naiveUtcMs).toISOString()
  let offset
  try {
    offset = tzOffsetMs(naiveUtcMs, tzId)
  } catch (err) {
    reportDiagnostic({
      severity: 'error',
      source: 'datetime.instantFromVenueLocal',
      title: 'Intl.DateTimeFormat threw',
      message: String(err?.message || err),
      details: `localStr=${localStr} tzId=${tzId}`,
    })
    return new Date(naiveUtcMs).toISOString()
  }
  let candidateMs = naiveUtcMs - offset
  const offset2 = tzOffsetMs(candidateMs, tzId)
  if (offset2 !== offset) candidateMs = naiveUtcMs - offset2
  return new Date(candidateMs).toISOString()
}

// Inverse of `instantFromVenueLocal`. Renders the UTC instant `iso` as the
// wall-clock string (`YYYY-MM-DDTHH:mm`) at `tzId`, suitable for an
// `<input type="datetime-local">` `value`.
export function venueLocalFromInstant(iso, tzId) {
  if (!iso || !tzId) return ''
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: tzId,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
    const parts = Object.fromEntries(
      dtf.formatToParts(new Date(iso)).map((p) => [p.type, p.value])
    )
    const hh = parts.hour === '24' ? '00' : parts.hour
    return `${parts.year}-${parts.month}-${parts.day}T${hh}:${parts.minute}`
  } catch (err) {
    reportDiagnostic({
      severity: 'error',
      source: 'datetime.venueLocalFromInstant',
      title: 'Intl.DateTimeFormat threw',
      message: String(err?.message || err),
      details: `iso=${iso} tzId=${tzId}`,
    })
    return ''
  }
}

// #308: the blue rail on a conference card shows a DATE RANGE, not a single
// talk's timestamp. Three shapes, because a dash is noise when both ends are
// the same day and ambiguous when the months differ:
//
//   same day      -> { weekday: 'Wed', monthDay: 'Sep 23' }
//   same month    -> { weekday: 'Wed–Thu', monthDay: 'Sep 23–24' }
//   across months -> { weekday: 'Tue–Thu', monthDay: 'Sep 30 – Oct 2' }
//
// Returns the same {weekday, monthDay} shape the single-date rail uses, so the
// card renders one block either way.
//
// En dash (–) not a hyphen: it is the correct glyph for a range, and it also
// stops "Sep 23-24" reading as a phone number to a screen reader.
export function formatDateRange(startIso, endIso, locale = undefined) {
  if (!startIso) return { weekday: '', monthDay: '' }
  try {
    const start = new Date(startIso)
    const end = endIso ? new Date(endIso) : start

    const wd = (d) => new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d)
    const md = (d) => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d)
    const day = (d) => new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(d)
    const monthOf = (d) => new Intl.DateTimeFormat(locale, { month: 'short' }).format(d)

    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()

    if (sameDay) return { weekday: wd(start), monthDay: md(start) }

    const sameMonth =
      start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()

    return {
      weekday: `${wd(start)}–${wd(end)}`,
      monthDay: sameMonth
        // "Sep 23–24" — repeating the month adds nothing.
        ? `${monthOf(start)} ${day(start)}–${day(end)}`
        // "Sep 30 – Oct 2" — spaces around the dash, or the months collide.
        : `${md(start)} – ${md(end)}`,
    }
  } catch (err) {
    reportDiagnostic({
      severity: 'error',
      source: 'datetime.formatDateRange',
      title: 'Intl.DateTimeFormat threw',
      message: String(err?.message || err),
      details: `start=${startIso} end=${endIso} locale=${locale}`,
    })
    return { weekday: '', monthDay: String(startIso).slice(0, 10) }
  }
}
